import React, { useCallback, useEffect, useMemo, useState } from 'react';

import debounce from 'lodash/debounce';

import { useTasksState } from '../helpers';
import { TasksContext } from './tasks_context';
import { getDefaults, isFilterEnabled, serializeFilters } from '../filters/helpers';
import { useQueryParamsState } from 'hooks/use_query_param_state';

import type { ReactNode } from 'react';
import type { ForUser, Task } from 'bundles/apex/tasks/types';
import type { Parent } from 'bundles/crm/types';
import type { Filters } from '../filters/types';
import type { Props as TaskProps } from '../task';
import type { TasksContextValue, TasksPaths } from './tasks_context';

const URL_PARAM_TYPES = {
  assigneeId: 'number',
  completed: 'boolean',
  dealUserId: 'number',
  fromDate: 'string',
  officeId: 'number',
  open: 'boolean',
  overdue: 'boolean',
  parentType: 'string',
  query: 'string',
  tbd: 'boolean',
  toDate: 'string',
  allTasks: 'boolean',
} as const;

export interface TasksProviderProps {
  children: ReactNode;
  currentParent?: Parent;
  features?: TaskProps['features'];
  filtersConfig: TasksContextValue['filtersConfig'];
  forUser: ForUser;
  hideAssignees?: boolean;
  options?: TasksContextValue['options'];
  paginate?: boolean;
  paths: TasksPaths;
  showParents?: TaskProps['showParents'];
  showRouterPrompt?: boolean;
}

export function TasksProvider({
  children,
  currentParent,
  features,
  filtersConfig,
  forUser,
  hideAssignees = false,
  options = {},
  paginate = false,
  paths,
  showParents,
  showRouterPrompt = false,
}: TasksProviderProps) {
  const state = useTasksState({ paginate, paths });

  const [showModal, setShowModal] = useState(false);

  const showTaskModal = useCallback((task?: Task) => {
    state.setSelectedTask(task || null);
    setShowModal(true);
  }, [state.setSelectedTask]);

  const enabledFilters = filtersConfig as unknown as Record<string, unknown>;
  const users = filtersConfig.options.users;

  const filterDefaults = useMemo<Filters>(() => getDefaults({
    assigneeId: (isFilterEnabled(enabledFilters, 'assignee') && users.find(user => user.id === forUser.id))
      ? forUser.id
      : null,
  }), []);

  const [filters, setFiltersState] = useQueryParamsState<Filters>(
    URL_PARAM_TYPES,
    filterDefaults,
    { prefix: 'tasks' }
  );

  useEffect(() => {
    state.setFilters(serializeFilters(filters));
  }, []);

  const debouncedDispatch = useMemo(
    () => debounce(state.setFilters, 500),
    [state.setFilters]
  );

  const changeFilters = useCallback((changes: Partial<Filters>, debouncing = false) => {
    const next = { ...filters, ...changes } as Filters;
    setFiltersState(next);
    state.setLoading(true);
    const callback = debouncing ? debouncedDispatch : state.setFilters;
    callback(serializeFilters(next));
  }, [filters, state.setFilters, debouncedDispatch, state.setLoading, setFiltersState]);

  const value = useMemo<TasksContextValue>(
    () => ({
      tasks: state.tasks,
      loading: state.loading,
      pagination: state.pagination,
      selectedTask: state.selectedTask,
      showModal,
      setFilters: state.setFilters,
      setLoading: state.setLoading,
      setPage: state.setPage,
      setSelectedTask: state.setSelectedTask,
      setShowModal,
      showTaskModal,
      reloadTasks: state.reloadTasks,
      createTask: state.createTask,
      updateTask: state.updateTask,
      deleteTask: state.deleteTask,
      addTaskAttachment: state.addTaskAttachment,
      removeTaskAttachment: state.removeTaskAttachment,
      changeFilters,
      currentParent,
      features,
      filters,
      filtersConfig,
      forUser,
      hideAssignees,
      options,
      paginate,
      paths,
      showParents,
      showRouterPrompt,
    }),
    [
      state.tasks,
      state.loading,
      state.pagination,
      state.selectedTask,
      state.setFilters,
      state.setLoading,
      state.setPage,
      state.setSelectedTask,
      state.reloadTasks,
      state.createTask,
      state.updateTask,
      state.deleteTask,
      state.addTaskAttachment,
      state.removeTaskAttachment,
      changeFilters,
      currentParent,
      features,
      filters,
      filtersConfig,
      forUser,
      hideAssignees,
      options,
      paginate,
      paths,
      showModal,
      showParents,
      showRouterPrompt,
      showTaskModal,
    ],
  );

  return (
    <TasksContext.Provider value={value}>
      {children}
    </TasksContext.Provider>
  );
}
